import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import {
    BookOpen, Trophy, ArrowLeft, Sparkles, Clock, ChevronLeft, ChevronRight,
    CheckCircle, XCircle, Home, Loader2, LogOut, Settings, BarChart3,
    X, ThumbsUp, ThumbsDown, Flag, ExternalLink, Shield, MessageSquare
} from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import { getAuth, onAuthStateChanged, GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
import TermsOfServiceModal from './TermsOfServiceModal';
import SettingsPage from './SettingsPage';
import ResultsPage from './ResultsPage';
import ReviewQuestionsPage from './ReviewQuestionsPage';
import AdminFeedbackPage from './AdminFeedbackPage';
import UserInboxPage from './UserInboxPage';
import CookieConsent from './CookieConsent';
import DailyLimitModal from './DailyLimitModal';
import { LogoOption1 } from './LogoComponents';

// ====================================================================
// API CONFIGURATION
// ====================================================================
const API_URL = process.env.REACT_APP_API_URL

// ====================================================================
// CITATION DISPLAY COMPONENT
// ====================================================================

// Transform heading to remove chapter numbers but keep topic info
// Examples:
//   "(See Chapter 2, DERMIS)" -> "See: DERMIS"
//   "(See Chapter 11 Patient Education Following Urinary/Fecal Diversion, Bathing)"
//     -> "See: Bathing (Patient Education Following Urinary/Fecal Diversion)"
const formatHeading = (heading) => {
    if (!heading) return null;

    // Remove outer parentheses if present
    let text = heading.trim();
    if (text.startsWith('(') && text.endsWith(')')) {
        text = text.slice(1, -1);
    }

    // Pattern 1: "See Chapter X, TOPIC" (simple format)
    const simpleMatch = text.match(/^See Chapter \d+,\s*(.+)$/i);
    if (simpleMatch) {
        return `See: ${simpleMatch[1].trim()}`;
    }

    // Pattern 2: "See Chapter X CHAPTER_TITLE, SUBTOPIC" (with chapter title)
    const complexMatch = text.match(/^See Chapter \d+\s+([^,]+),\s*(.+)$/i);
    if (complexMatch) {
        const chapterTitle = complexMatch[1].trim();
        const subtopic = complexMatch[2].trim();
        return `See: ${subtopic} (${chapterTitle})`;
    }

    // Fallback: return as-is without "See Chapter X" if present
    const fallbackMatch = text.match(/^See Chapter \d+\s*(.*)$/i);
    if (fallbackMatch) {
        return `See: ${fallbackMatch[1].trim() || text}`;
    }

    return text;
};

const CitationDisplay = ({ heading, citation }) => {
    if (!heading && !citation) return null;

    const formattedHeading = formatHeading(heading);

    return (
        <div className="mt-6 pt-4 border-t-2 border-gray-200">
            {/* Heading - Where to read more */}
            {formattedHeading && (
                <div className="mb-3 flex items-start gap-2">
                    <BookOpen className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-semibold text-gray-700 mb-1">
                            Read More:
                        </p>
                        <p className="text-sm text-gray-600 italic">
                            {formattedHeading}
                        </p>
                    </div>
                </div>
            )}

            {/* Citation - Source reference */}
            {citation && (
                <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded-r-lg">
                    <p className="text-xs font-semibold text-blue-900 mb-1">
                        SOURCE:
                    </p>
                    {citation.link ? (
                        <a
                            href={citation.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-700 hover:text-blue-900 hover:underline flex items-start gap-2 group"
                        >
                            <span className="flex-1">{citation.text}</span>
                            <ExternalLink className="w-4 h-4 flex-shrink-0 mt-0.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </a>
                    ) : (
                        <p className="text-sm text-blue-700">
                            {citation.text}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

// ====================================================================
// 1. FIREBASE CONFIG & AUTH (UNCHANGED)
// ====================================================================

let localConfig;
try {
    localConfig = require('./firebase.local.js');
} catch {
    // No local config, that's fine
}

const productionConfig = {
    apiKey: "AIzaSyDiJ6YgoQ7edlytUby24NiQ3nd1Ro2ff58",
    authDomain: "woundostomyprep.org",
    projectId: "woundostomyprep-prod",
    storageBucket: "woundostomyprep-prod.firebasestorage.app",
    messagingSenderId: "158784895890",
    appId: "1:158784895890:web:1d2124022206f63188df4b",
    measurementId: "G-ZXCELH09MR"
};


// Determine which config to use
const getFirebaseConfig = () => {

    // 1. Use environment variable to distinguish staging vs prod builds
    if (process.env.REACT_APP_FIREBASE_ENV === 'prod') {
        console.log('Using  Production Firebase config');
        return productionConfig;
    }

    // 2. Local override for npm start
    if (localConfig) {
        console.log('Using local firebase.local.js config');
        return localConfig.default || localConfig;
    }

    // 3. Fallback: If no config is found, log and crash
    const errorMessage =
        'FATAL: No Firebase configuration found. ' +
        'Set REACT_APP_FIREBASE_ENV (to "staging" or "production") ' +
        'or create a firebase.local.js file for local development.';

    console.error(errorMessage);
    throw new Error(errorMessage);

}

const app = initializeApp(getFirebaseConfig());

// ====================================================================
// FIREBASE ANALYTICS WITH CONSENT MODE
// ====================================================================
// Set default consent to DENIED before initializing analytics
// This is GDPR/CCPA compliant - user must opt in
if (typeof window !== 'undefined') {
    // Initialize gtag with consent mode
    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = gtag;

    // Set default consent to denied
    gtag('consent', 'default', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied'
    });

    // Check if user has already consented
    const existingConsent = localStorage.getItem('cookieConsent');
    if (existingConsent === 'accepted') {
        // User previously accepted, update consent
        gtag('consent', 'update', {
            'analytics_storage': 'granted'
        });
    }
}

// Now initialize analytics (will respect consent mode)
const analytics = getAnalytics(app);
const auth = getAuth(app);

// Make analytics available globally for the CookieConsent component
if (typeof window !== 'undefined') {
    window.firebaseAnalytics = analytics;
}

// Helper function to log analytics events (only if user consented)
const logAnalyticsEvent = (eventName, eventParams = {}) => {
    const consent = localStorage.getItem('cookieConsent');
    if (consent === 'accepted' && window.firebaseAnalytics) {
        const { logEvent } = require('firebase/analytics');
        logEvent(window.firebaseAnalytics, eventName, eventParams);
    }
};

const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tosAccepted, setTosAccepted] = useState(false);
    const [showTosModal, setShowTosModal] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setUser(user);

            if (user) {
                // Check if this is a new user (account created within last 10 seconds)
                const creationTime = new Date(user.metadata.creationTime).getTime();
                const now = Date.now();
                const isNewUser = (now - creationTime) < 10000; // Within 10 seconds

                // Log analytics event: New user sign up
                if (isNewUser) {
                    logAnalyticsEvent('sign_up', {
                        method: 'google'
                    });
                }

                // User is signed in, check ToS acceptance from backend
                await checkTosAcceptance(user);
            } else {
                // User is signed out
                setTosAccepted(false);
                setShowTosModal(false);
                setIsAdmin(false);
                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, []);

    const checkTosAcceptance = async (user) => {
        try {
            // Get Firebase ID token
            const token = await user.getIdToken(true);

            // Call backend to check ToS acceptance
            const response = await fetch(`${API_URL}/api/tos/check`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to check ToS acceptance');
            }

            const data = await response.json();

            if (data.accepted) {
                // User has accepted ToS
                setTosAccepted(true);
                setShowTosModal(false);

                // Fetch user profile to get admin status
                await fetchUserProfile(token);
            } else {
                // User has NOT accepted ToS - show modal
                setTosAccepted(false);
                setShowTosModal(true);
            }
        } catch (error) {
            console.error('Error checking ToS acceptance:', error);
            // Fallback: Show ToS modal if we can't check
            setTosAccepted(false);
            setShowTosModal(true);
        } finally {
            setLoading(false);
        }
    };

    const fetchUserProfile = async (token) => {
        try {
            const response = await fetch(`${API_URL}/api/account/me`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setIsAdmin(data.is_admin);
            }
        } catch (error) {
            console.error('Error fetching user profile:', error);
            setIsAdmin(false);
        }
    };

    const handleTosAccept = async () => {
        if (!user) return;

        try {
            // Get Firebase ID token
            const token = await user.getIdToken(true);

            // Call backend to record acceptance
            const response = await fetch(`${API_URL}/api/tos/accept`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to record ToS acceptance');
            }

            // Success! Update state
            setTosAccepted(true);
            setShowTosModal(false);

            // Now fetch user profile to get admin status
            await fetchUserProfile(token);

        } catch (error) {
            console.error('Error recording ToS acceptance:', error);
            alert('Failed to record acceptance. Please try again.');
        }
    };

    const handleTosDecline = () => {
        setShowTosModal(false);
        signOut(auth);
    };

    const value = { user, loading, tosAccepted, isAdmin };

    if (loading) {
        return <div className="min-h-screen bg-gray-900 flex items-center justify-center">
            <Loader2 className="w-12 h-12 text-white animate-spin" />
        </div>;
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
            {/* NEW: Add the ToS modal */}
            <TermsOfServiceModal
                isOpen={showTosModal}
                onAccept={handleTosAccept}
                onDecline={handleTosDecline}
            />
        </AuthContext.Provider>
    );
};

const useAuth = () => useContext(AuthContext);
const getAuthToken = async (user) => {
    if (!user) return null;
    try {
        return await user.getIdToken(true);
    } catch (error) {
        console.error("Error getting auth token:", error);
        return null;
    }
};

// ====================================================================
// 2. REORDERED UI COMPONENTS
// ====================================================================

const StudySummaryModal = ({ stats, onButtonClick, title, buttonText }) => {
    const accuracy = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0;
    let performanceColor = 'text-yellow-500';
    if (accuracy >= 80) performanceColor = 'text-green-500';
    else if (accuracy < 50) performanceColor = 'text-red-500';

    return (
        <>
            <style>{`
                @keyframes scale-in {
                    0% { opacity: 0; transform: scale(0.95); }
                    100% { opacity: 1; transform: scale(1); }
                }
                .animate-scale-in {
                    animation: scale-in 0.3s ease-out forwards;
                }
            `}</style>
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                <div className="bg-white/95 rounded-3xl shadow-2xl w-full max-w-md text-center p-8 animate-scale-in">
                    <div className="mx-auto bg-gradient-to-br from-blue-500 to-cyan-500 w-20 h-20 rounded-full flex items-center justify-center -mt-20 mb-6 border-4 border-white">
                        <Trophy className="w-10 h-10 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-800 mb-2">{title}</h2>
                    <p className="text-gray-600 mb-8">Here's how you did:</p>
                    <div className="grid grid-cols-3 gap-4 text-center mb-8">
                        <div><p className="text-4xl font-bold text-blue-600">{stats.total}</p><p className="text-sm text-gray-500">Answered</p></div>
                        <div><p className="text-4xl font-bold text-green-600">{stats.correct}</p><p className="text-sm text-gray-500">Correct</p></div>
                        <div><p className={`text-4xl font-bold ${performanceColor}`}>{accuracy}%</p><p className="text-sm text-gray-500">Accuracy</p></div>
                    </div>
                    <button
                        onClick={onButtonClick}
                        className="w-full flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:shadow-lg hover:scale-105 transition-all"
                    >
                        {buttonText}
                    </button>
                </div>
            </div>
        </>
    );
};

// ====================================================================
// 3. NEW FEEDBACK MODAL COMPONENT
// ====================================================================

const FeedbackModal = ({ show, onClose, questionId, questionVersion = 1, onSubmitFeedback }) => {
    const [feedbackType, setFeedbackType] = useState(null); // 'like', 'dislike', 'neutral'
    const [comment, setComment] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);

    // Reset form when modal is closed or 'show' prop changes
    useEffect(() => {
        if (!show) {
            setTimeout(() => { // Allow fade-out animation
                setFeedbackType(null);
                setComment('');
                setIsSubmitted(false);
            }, 300);
        }
    }, [show]);

    const handleSubmit = () => {
        // Call parent's feedback submission handler (makes API call)
        onSubmitFeedback({
            question_id: questionId,
            type: feedbackType,
            comment: comment,
            question_version: questionVersion,
        });
        setIsSubmitted(true);
        // Auto-close after 2 seconds
        setTimeout(onClose, 2000);
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-scale-in">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                    <X className="w-6 h-6" />
                </button>

                {isSubmitted ? (
                    <div className="text-center p-8">
                        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Thank You!</h2>
                        <p className="text-gray-600">Your feedback has been received.</p>
                    </div>
                ) : (
                    <>
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">Provide Feedback</h2>
                        <p className="text-gray-600 mb-4">What did you think of this question?</p>

                        <div className="flex gap-3 mb-4">
                            <button
                                onClick={() => setFeedbackType('like')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${feedbackType === 'like' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:bg-gray-50'}`}
                            >
                                <ThumbsUp className={`w-5 h-5 ${feedbackType === 'like' ? 'text-green-600' : 'text-gray-500'}`} />
                                <span className={`font-semibold ${feedbackType === 'like' ? 'text-green-700' : 'text-gray-600'}`}>Like</span>
                            </button>
                            <button
                                onClick={() => setFeedbackType('dislike')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${feedbackType === 'dislike' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:bg-gray-50'}`}
                            >
                                <ThumbsDown className={`w-5 h-5 ${feedbackType === 'dislike' ? 'text-red-600' : 'text-gray-500'}`} />
                                <span className={`font-semibold ${feedbackType === 'dislike' ? 'text-red-700' : 'text-gray-600'}`}>Dislike</span>
                            </button>
                        </div>

                        <div className="mb-4">
                            <button
                                onClick={() => setFeedbackType('neutral')}
                                className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all ${feedbackType === 'neutral' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
                            >
                                <div className="flex items-center gap-2">
                                    <Flag className={`w-5 h-5 ${feedbackType === 'neutral' ? 'text-blue-600' : 'text-gray-500'}`} />
                                    <span className={`font-semibold ${feedbackType === 'neutral' ? 'text-blue-700' : 'text-gray-600'}`}>Report an Issue</span>
                                </div>
                            </button>
                        </div>

                        {feedbackType && (
                            <div className="mb-4 animate-scale-in">
                                <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2">
                                    Please describe the issue (e.g., typo, incorrect answer, unclear question):
                                </label>
                                <textarea
                                    id="comment"
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    rows="3"
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Optional details..."
                                />
                            </div>
                        )}

                        <button
                            onClick={handleSubmit}
                            disabled={!feedbackType || (feedbackType === 'neutral' && !comment)}
                            className="w-full px-8 py-3 rounded-xl font-semibold bg-gradient-to-r from-blue-500 to-cyan-500 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition-all"
                        >
                            Submit Feedback
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};


// ====================================================================
// 4. UPDATED QUIZ & STUDY COMPONENTS
// ====================================================================

const QuizApp = ({ onBackToHome, studyCategory }) => {
    const { user } = useAuth();
    const [sessionId, setSessionId] = useState(null);
    const [questions, setQuestions] = useState([]);  // Questions now have 'uuid' instead of 'id'
    const [answers, setAnswers] = useState({});  // Keyed by question UUID
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [quizState, setQuizState] = useState('loading');
    const [timer, setTimer] = useState(0);
    const [error, setError] = useState(null);
    const [quizResults, setQuizResults] = useState(null);

    // --- NEW STATE ---
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const [showDailyLimit, setShowDailyLimit] = useState(false);

    useEffect(() => {
        const startNewQuiz = async () => {
            try {
                const token = await getAuthToken(user);
                const response = await fetch(`${API_URL}/quiz/start`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ category: studyCategory })
                });

                // Handle daily limit (429)
                if (response.status === 429) {
                    setShowDailyLimit(true);
                    return;
                }

                if (!response.ok) {
                    const errData = await response.json();
                    throw new Error(errData.detail || "Failed to start quiz.");
                }
                const data = await response.json();
                setSessionId(data.session_id);
                setQuestions(data.questions);  // Questions now have 'uuid' field
                setQuizState('active');

                // Log analytics event: Quiz started
                logAnalyticsEvent('quiz_started', {
                    session_id: data.session_id
                });
            } catch (err) {
                setError(err.message);
                setQuizState('error');
            }
        };
        startNewQuiz();
    }, [user]);

    useEffect(() => {
        if (quizState !== 'active') return;
        const interval = setInterval(() => setTimer(t => t + 1), 1000);
        return () => clearInterval(interval);
    }, [quizState]);

    const handleFinish = async () => {
        setQuizState('submitting');

        // Build submission with question_uuid instead of question_id
        const submissionPayload = {
            answers: questions.map(q => {
                const answerIndex = answers[q.uuid];
                const user_selection = answerIndex !== undefined
                    ? ['A', 'B', 'C', 'D'][answerIndex].toLowerCase()
                    : null;
                return { question_uuid: q.uuid, user_selection: user_selection };
            })
        };

        try {
            const token = await getAuthToken(user);
            const response = await fetch(`${API_URL}/quiz/${sessionId}/finish`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(submissionPayload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error("Server Error:", errorData);
                throw new Error("Failed to submit quiz answers.");
            }

            const resultsData = await response.json();
            setQuizResults(resultsData);
            setQuizState('report_card');

            // Log analytics event: Quiz completed
            logAnalyticsEvent('quiz_completed', {
                session_id: sessionId,
                score: resultsData.score,
                total_questions: resultsData.results.length
            });
        } catch (err) {
            setError(err.message);
            setQuizState('error');
        }
    };


    const handleFeedbackSubmit = async (feedbackData) => {
        console.log("Submitting feedback:", feedbackData);

        try {
            const token = await getAuthToken(user);
            if (!token) {
                console.error("No auth token found, cannot submit feedback.");
                return;
            }

            const response = await fetch(`${API_URL}/feedback/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    question_id: feedbackData.question_id,
                    session_id: sessionId,
                    type: feedbackData.type,
                    comment: feedbackData.comment,
                    question_version: feedbackData.question_version || 1
                })
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.detail || "Failed to submit feedback.");
            }

            // The modal will close itself after submission succeeds
            // (based on the logic inside FeedbackModal)
            console.log("Feedback submitted successfully.");

            // Log analytics event: Feedback submitted
            logAnalyticsEvent('feedback_submitted', {
                type: feedbackData.type,
                question_id: feedbackData.question_id,
                session_id: sessionId,
                mode: 'quiz'
            });

        } catch (err) {
            console.error("Error submitting feedback:", err);
            // Optionally, show an error message to the user
            alert(`Error: ${err.message}`);
        }
    };

    const handleAnswerSelect = (qUuid, optIdx) => setAnswers(prev => ({ ...prev, [qUuid]: optIdx }));
    const handleNext = () => {
        setCurrentQuestionIndex(i => Math.min(i + 1, questions.length - 1));
        setIsFeedbackModalOpen(false); // Close modal on nav
    };
    const handlePrevious = () => {
        setCurrentQuestionIndex(i => Math.max(i - 1, 0));
        setIsFeedbackModalOpen(false); // Close modal on nav
    };
    const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

    // Daily limit reached
    if (showDailyLimit) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center">
                <DailyLimitModal show={true} onGoHome={onBackToHome} />
            </div>
        );
    }

    // Loading and error states
    if (quizState === 'loading' || quizState === 'submitting') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center">
                <Loader2 className="w-12 h-12 text-white animate-spin" />
            </div>
        );
    }

    if (quizState === 'error') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-500 to-orange-500 text-white flex flex-col items-center justify-center p-4">
                <XCircle className="w-16 h-16 mb-4" />
                <h2 className="text-3xl font-bold mb-2">Error</h2>
                <p className="text-xl mb-6">{error}</p>
                <button onClick={onBackToHome} className="px-8 py-3 bg-white text-red-800 rounded-xl font-semibold hover:shadow-lg transition-all">
                    Back to Home
                </button>
            </div>
        );
    }

    // Get current question data based on state
    const isReviewMode = quizState === 'review';

    if (!questions || questions.length === 0) {
        return <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center"><Loader2 className="w-12 h-12 text-white animate-spin" /></div>;
    }

    const activeQuestion = isReviewMode
        ? quizResults.results[currentQuestionIndex]
        : questions[currentQuestionIndex];

    if (!activeQuestion) {
        return <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center"><Loader2 className="w-12 h-12 text-white animate-spin" /></div>;
    }

    // Handle different data structures - now using UUID
    const questionText = isReviewMode ? activeQuestion.question_text : activeQuestion.question;
    const questionUuid = isReviewMode ? activeQuestion.question_uuid : activeQuestion.uuid;
    const questionOptions = isReviewMode
        ? activeQuestion.options
        : [
            activeQuestion.choice_a,
            activeQuestion.choice_b,
            activeQuestion.choice_c,
            activeQuestion.choice_d
        ].filter(opt => opt !== null && opt !== undefined);

    const userSelectionIndex = isReviewMode
        ? (activeQuestion.user_selection ? ['A', 'B', 'C', 'D'].indexOf(activeQuestion.user_selection.toUpperCase()) : null)
        : answers[questionUuid];

    const correctAnswerIndex = isReviewMode
        ? ['A', 'B', 'C', 'D'].indexOf(activeQuestion.correct_answer.toUpperCase())
        : null;

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-4 flex items-center justify-center">
            {/* Show modal overlay when in report_card state */}
            {quizState === 'report_card' && quizResults && (
                <StudySummaryModal
                    stats={{
                        correct: quizResults.correct_count,
                        total: quizResults.total_questions
                    }}
                    onButtonClick={() => {
                        setQuizState('review');
                        setCurrentQuestionIndex(0);
                        setIsFeedbackModalOpen(false); // Reset on mode change
                    }}
                    title="Quiz Complete!"
                    buttonText="Review Answers"
                />
            )}

            {/* --- RENDER NEW MODAL --- */}
            <FeedbackModal
                show={isFeedbackModalOpen}
                onClose={() => setIsFeedbackModalOpen(false)}
                questionId={questionUuid}
                questionVersion={activeQuestion.version || 1}
                onSubmitFeedback={handleFeedbackSubmit}
            />

            <div className="w-full max-w-3xl">
                {/* Header */}
                <div className="bg-white/95 rounded-t-3xl p-6 shadow-xl">
                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-800">
                                {isReviewMode ? "Quiz Review" : "Quiz Challenge"}
                            </h1>
                            <p className="text-sm text-gray-500">
                                Question {currentQuestionIndex + 1} of {questions.length}
                            </p>
                        </div>
                        {quizState === 'active' && (
                            <div className="flex items-center gap-2 bg-indigo-100 px-4 py-2 rounded-full">
                                <Clock className="w-5 h-5 text-indigo-600" />
                                <span className="font-mono text-lg font-semibold text-indigo-600">
                                    {formatTime(timer)}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Question and Options */}
                <div className="bg-white/95 p-8 shadow-xl">
                    <h2 className="text-xl font-normal text-gray-900 mb-8 leading-relaxed">
                        {questionText}
                    </h2>
                    <div className="space-y-3">
                        {questionOptions.map((option, index) => {
                            if (option === null) return null;

                            let buttonStyle = 'border-gray-200 bg-white hover:border-indigo-300';

                            if (isReviewMode) {
                                if (index === correctAnswerIndex) {
                                    buttonStyle = 'border-green-500 bg-green-50';
                                } else if (index === userSelectionIndex) {
                                    buttonStyle = 'border-red-500 bg-red-50';
                                } else {
                                    buttonStyle = 'border-gray-200 bg-gray-100 opacity-60';
                                }
                            } else if (index === userSelectionIndex) {
                                buttonStyle = 'border-indigo-500 bg-indigo-50 shadow-md';
                            }

                            return (
                                <button
                                    key={index}
                                    onClick={() => quizState === 'active' && handleAnswerSelect(questionUuid, index)}
                                    disabled={quizState !== 'active'}
                                    className={`w-full text-left p-5 rounded-xl border-2 transition-all ${buttonStyle}`}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`w-8 h-8 min-w-[2rem] aspect-square rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isReviewMode && index === correctAnswerIndex ? 'border-green-500 bg-green-500' :
                                            isReviewMode && index === userSelectionIndex ? 'border-red-500 bg-red-500' :
                                                !isReviewMode && index === userSelectionIndex ? 'border-indigo-500 bg-indigo-500' :
                                                    'border-gray-300'
                                            }`}>
                                            {isReviewMode && index === correctAnswerIndex && <CheckCircle className="w-5 h-5 text-white" />}
                                            {isReviewMode && index === userSelectionIndex && index !== correctAnswerIndex && <XCircle className="w-5 h-5 text-white" />}
                                            {!isReviewMode && index === userSelectionIndex && <CheckCircle className="w-5 h-5 text-white" />}
                                        </div>
                                        <span className={`text-base font-normal ${isReviewMode && index === correctAnswerIndex ? 'font-medium text-green-700' :
                                            isReviewMode && index === userSelectionIndex ? 'font-medium text-red-700' :
                                                !isReviewMode && index === userSelectionIndex ? 'font-medium text-indigo-700' :
                                                    'text-gray-800'
                                            }`}>
                                            {option}
                                        </span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Explanation (Review mode only) */}
                {isReviewMode && activeQuestion.explanation && (
                    <div className="bg-white/95 p-6 border-t-2 border-gray-100">
                        <div className="flex items-start gap-3">
                            <BookOpen className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                            <div className="flex-1">
                                <h3 className="text-lg font-bold text-gray-800 mb-2">Explanation</h3>
                                <p className="text-gray-700 leading-relaxed">{activeQuestion.explanation}</p>

                                {/* Citation Display - citation is embedded directly */}
                                <CitationDisplay
                                    heading={activeQuestion.heading}
                                    citation={activeQuestion.citation}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Navigation Footer */}
                <div className="bg-white/95 rounded-b-3xl p-6 shadow-xl">

                    {/* Feedback Button - Full Width Above Navigation */}
                    <div className="mb-4 pb-4 border-b border-gray-200">
                        <button
                            onClick={() => setIsFeedbackModalOpen(true)}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-base bg-blue-50 text-blue-700 hover:bg-blue-100 hover:shadow-md transition-all"
                        >
                            <Flag className="w-5 h-5" />
                            Report an Issue or Provide Feedback
                        </button>
                    </div>

                    {/* Navigation Buttons */}
                    <div className="flex justify-between items-center">
                        <button
                            onClick={handlePrevious}
                            disabled={currentQuestionIndex === 0}
                            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${currentQuestionIndex === 0
                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                        >
                            <ChevronLeft className="w-5 h-5" /> Previous
                        </button>

                        {isReviewMode ? (
                            currentQuestionIndex === questions.length - 1 ? (
                                <button
                                    onClick={onBackToHome}
                                    className="flex items-center gap-2 px-8 py-3 rounded-xl font-semibold bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:shadow-lg hover:scale-105 transition-all"
                                >
                                    <Home className="w-5 h-5" /> Back to Home
                                </button>
                            ) : (
                                <button
                                    onClick={handleNext}
                                    className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all"
                                >
                                    Next <ChevronRight className="w-5 h-5" />
                                </button>
                            )
                        ) : (
                            currentQuestionIndex === questions.length - 1 ? (
                                <button
                                    onClick={handleFinish}
                                    className="flex items-center gap-2 px-8 py-3 rounded-xl font-semibold bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:shadow-lg hover:scale-105 transition-all"
                                >
                                    <CheckCircle className="w-5 h-5" /> Finish Quiz
                                </button>
                            ) : (
                                <button
                                    onClick={handleNext}
                                    className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all"
                                >
                                    Next <ChevronRight className="w-5 h-5" />
                                </button>
                            )
                        )}
                    </div>
                </div>

                {/* Question indicators */}
                <div className="flex justify-center gap-2 mt-6 flex-wrap">
                    {questions.map((q, index) => (
                        <button
                            key={q.uuid}
                            onClick={() => {
                                setCurrentQuestionIndex(index);
                                setIsFeedbackModalOpen(false); // Close modal on nav
                            }}
                            className={`w-10 h-10 rounded-full font-semibold transition-all ${index === currentQuestionIndex
                                ? 'bg-white text-indigo-600 shadow-lg scale-110'
                                : isReviewMode && quizResults.results[index].is_correct
                                    ? 'bg-green-500 text-white hover:bg-green-600'
                                    : isReviewMode && !quizResults.results[index].is_correct
                                        ? 'bg-red-500 text-white hover:bg-red-600'
                                        : answers[q.uuid] !== undefined
                                            ? 'bg-white/70 text-indigo-600'
                                            : 'bg-white/30 text-white hover:bg-white/50'
                                }`}
                        >
                            {index + 1}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

const StudyQuestions = ({ onFinishSession, studyCategory }) => {
    // --- State Management ---
    const { user } = useAuth();
    const [sessionId, setSessionId] = useState(null);
    const [questionData, setQuestionData] = useState(null);  // Now has 'uuid' instead of 'id'
    const [citations, setCitations] = useState([]); // NEW: Store citations
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const [timer, setTimer] = useState(0);
    const [stats, setStats] = useState({ correct: 0, total: 0 });
    const [showSummary, setShowSummary] = useState(false);

    // --- Question Queue State ---
    const [questionQueue, setQuestionQueue] = useState([]);  // Now stores UUIDs
    const [, setTotalAvailable] = useState(0);

    // --- NEW STATE ---
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const [showDailyLimit, setShowDailyLimit] = useState(false);

    // --- Data Transformation ---
    const transformApiQuestion = (apiQuestion) => {
        const options = [
            apiQuestion.choice_a,
            apiQuestion.choice_b,
            apiQuestion.choice_c,
            apiQuestion.choice_d,
        ].filter(o => o !== null);

        const answerMap = { 'A': 0, 'B': 1, 'C': 2, 'D': 3 };
        const correctAnswerIndex = answerMap[apiQuestion.answer.toUpperCase()];

        return {
            uuid: apiQuestion.uuid,  // UUID instead of id
            version: apiQuestion.version || 1,  // Question version for A/B testing
            question: apiQuestion.question,
            options,
            correctAnswer: correctAnswerIndex,
            explanation: apiQuestion.explanation,
            heading: apiQuestion.heading, // NEW: Include heading
            citation_id: apiQuestion.citation_id, // NEW: Include citation_id
        };
    };

    // NEW: Helper function to get citation by ID
    const getCitation = (citationId) => {
        return citations.find(c => c.id === citationId);
    };

    // --- API Calls & Side Effects ---
    const handleFinish = useCallback(async () => {
        if (sessionId) {
            try {
                const token = await getAuthToken(user);
                await fetch(`${API_URL}/sessions/${sessionId}/complete`, {
                    method: 'PATCH',
                    headers: { 'Authorization': `Bearer ${token}` },
                });
            } catch (err) {
                console.error("Failed to mark session as complete:", err);
            }
        }
        setLoading(false);
        setShowSummary(true);
    }, [sessionId, user]);

    const fetchQuestionByUuid = useCallback(async (sid, qUuid) => {
        if (!sid || !qUuid) return;
        setLoading(true);
        try {
            const token = await getAuthToken(user);
            const response = await fetch(`${API_URL}/questions/${qUuid}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!response.ok) throw new Error("Failed to fetch question data.");
            const data = await response.json();
            setQuestionData(transformApiQuestion(data));
            // NEW: Load citations if provided
            if (data.citations) {
                setCitations(data.citations);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [user]);

    // Fetch a batch of question UUIDs from the backend
    const fetchQuestionBatch = useCallback(async () => {
        try {
            const token = await getAuthToken(user);
            const response = await fetch(`${API_URL}/sessions/study`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ category: studyCategory })
            });

            // Handle daily limit (429)
            if (response.status === 429) {
                setShowDailyLimit(true);
                setLoading(false);
                return { sessionId: null, questionUuids: [], dailyLimitReached: true };
            }

            if (!response.ok) throw new Error("Could not fetch questions.");
            const sessionData = await response.json();
            setSessionId(sessionData.session_id);
            setTotalAvailable(sessionData.total_available);
            return {
                sessionId: sessionData.session_id,
                questionUuids: sessionData.question_uuids || []  // UUIDs instead of IDs
            };
        } catch (err) {
            setError(err.message);
            return { sessionId: null, questionUuids: [] };
        }
    }, [user, studyCategory]);

    useEffect(() => {
        if (!user) return;
        const initializeSession = async () => {
            setLoading(true);
            try {
                const { sessionId: sid, questionUuids, dailyLimitReached } = await fetchQuestionBatch();

                // If daily limit reached, the modal is already shown
                if (dailyLimitReached) {
                    return;
                }

                // Log analytics event: Study session started
                logAnalyticsEvent('study_session_started', {
                    session_id: sid
                });

                if (questionUuids.length > 0) {
                    // Store queue and fetch first question (using UUIDs)
                    const [firstUuid, ...rest] = questionUuids;
                    setQuestionQueue(rest);
                    fetchQuestionByUuid(sid, firstUuid);
                } else {
                    handleFinish();
                }
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        };
        initializeSession();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    useEffect(() => {
        const interval = setInterval(() => setTimer(prev => prev + 1), 1000);
        return () => clearInterval(interval);
    }, [questionData]);

    // --- Event Handlers ---
    const formatTime = (seconds) => `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;

    const handleAnswerSelect = (index) => {
        if (!showFeedback) setSelectedAnswer(index);
    };

    const handleSubmit = async () => {
        if (selectedAnswer === null) return;
        setShowFeedback(true);
        const isCorrect = selectedAnswer === questionData.correctAnswer;
        setStats(prev => ({ correct: prev.correct + (isCorrect ? 1 : 0), total: prev.total + 1 }));
        try {
            const token = await getAuthToken(user);
            await fetch(`${API_URL}/sessions/${sessionId}/responses`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    question_uuid: questionData.uuid,  // UUID instead of ID
                    user_selection: ['A', 'B', 'C', 'D'][selectedAnswer],
                    is_correct: isCorrect,
                    question_version: questionData.version,  // Track A/B version
                }),
            });
        } catch (err) {
            console.error("Failed to save response:", err);
        }
    };

    // --- NEW HANDLER ---
    const handleFeedbackSubmit = async (feedbackData) => {
        console.log("Submitting feedback:", feedbackData);

        try {
            const token = await getAuthToken(user);
            if (!token) {
                console.error("No auth token found, cannot submit feedback.");
                return;
            }

            const response = await fetch(`${API_URL}/feedback/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    question_id: feedbackData.question_id,
                    session_id: sessionId,
                    type: feedbackData.type,
                    comment: feedbackData.comment,
                    question_version: feedbackData.question_version || 1
                })
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.detail || "Failed to submit feedback.");
            }

            // The modal will close itself after submission succeeds
            // (based on the logic inside FeedbackModal)
            console.log("Feedback submitted successfully.");

            // Log analytics event: Feedback submitted
            logAnalyticsEvent('feedback_submitted', {
                type: feedbackData.type,
                question_id: feedbackData.question_id,
                session_id: sessionId,
                mode: 'study'
            });

        } catch (err) {
            console.error("Error submitting feedback:", err);
            // Optionally, show an error message to the user
            alert(`Error: ${err.message}`);
        }
    };


    const handleNext = async () => {
        setSelectedAnswer(null);
        setShowFeedback(false);
        setTimer(0);
        setIsFeedbackModalOpen(false); // Close modal on nav

        // Check if we have questions in the queue (UUIDs)
        if (questionQueue.length > 0) {
            // Use next question from queue
            const [nextUuid, ...rest] = questionQueue;
            setQuestionQueue(rest);
            fetchQuestionByUuid(sessionId, nextUuid);
        } else {
            // Queue exhausted, fetch a new batch
            setLoading(true);
            const { questionUuids } = await fetchQuestionBatch();
            if (questionUuids.length > 0) {
                const [nextUuid, ...rest] = questionUuids;
                setQuestionQueue(rest);
                fetchQuestionByUuid(sessionId, nextUuid);
            } else {
                // No more questions available
                handleFinish();
            }
        }
    };

    // --- Conditional Rendering ---
    // Daily limit reached
    if (showDailyLimit) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-500 via-cyan-500 to-teal-500 flex items-center justify-center">
                <DailyLimitModal show={true} onGoHome={onFinishSession} />
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-500 to-teal-500 p-4 flex flex-col items-center justify-center text-white">
                <Loader2 className="w-16 h-16 animate-spin mb-4" />
                <p className="text-2xl font-semibold">Loading...</p>
            </div>
        );
    }
    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-500 to-orange-500 p-4 flex flex-col items-center justify-center text-white text-center">
                <XCircle className="w-16 h-16 mb-4" />
                <h2 className="text-3xl font-bold mb-2">An Error Occurred</h2>
                <p className="text-xl mb-6">{error}</p>
                <button onClick={onFinishSession} className="flex items-center gap-2 px-8 py-3 rounded-xl font-semibold bg-white text-red-600 hover:shadow-lg hover:scale-105 transition-all">
                    <Home className="w-5 h-5" /> Back to Home
                </button>
            </div>
        );
    }
    if (!questionData) return null;

    // --- Main Render ---
    const isCorrect = selectedAnswer === questionData.correctAnswer;
    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-500 via-cyan-500 to-teal-500 p-4 flex items-center justify-center">
            {showSummary && <StudySummaryModal stats={stats} onButtonClick={onFinishSession} title="Study Session Complete!" buttonText="Back to Home" />}

            {/* --- RENDER NEW MODAL --- */}
            <FeedbackModal
                show={isFeedbackModalOpen}
                onClose={() => setIsFeedbackModalOpen(false)}
                questionId={questionData.uuid}
                questionVersion={questionData.version || 1}
                onSubmitFeedback={handleFeedbackSubmit}
            />

            <div className="w-full max-w-3xl">
                {/* Header */}
                <div className="bg-white/95 backdrop-blur-sm rounded-t-3xl p-6 shadow-xl">
                    <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-3">
                            <div className="bg-blue-100 p-3 rounded-full"><BookOpen className="w-6 h-6 text-blue-600" /></div>
                            <h1 className="text-2xl font-bold text-gray-800">Study Questions</h1>
                        </div>
                        <div className="flex items-center gap-2 bg-blue-100 px-4 py-2 rounded-full">
                            <Clock className="w-5 h-5 text-blue-600" />
                            <span className="font-mono text-lg font-semibold text-blue-600">{formatTime(timer)}</span>
                        </div>
                    </div>
                    {stats.total > 0 && (
                        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-4 flex justify-around">
                            <div className="text-center"><p className="text-sm text-gray-600">Answered</p><p className="text-2xl font-bold text-blue-600">{stats.total}</p></div>
                            <div className="text-center"><p className="text-sm text-gray-600">Correct</p><p className="text-2xl font-bold text-green-600">{stats.correct}</p></div>
                            <div className="text-center"><p className="text-sm text-gray-600">Accuracy</p><p className="text-2xl font-bold text-cyan-600">{Math.round((stats.correct / stats.total) * 100)}%</p></div>
                        </div>
                    )}
                </div>

                {/* Question Body */}
                <div className="bg-white/95 backdrop-blur-sm p-8 shadow-xl">
                    <h2 className="text-xl font-normal text-gray-900 mb-8 leading-relaxed">{questionData.question}</h2>
                    <div className="space-y-3 mb-6">
                        {questionData.options.map((option, index) => {
                            const isSelected = selectedAnswer === index;
                            const isCorrectAnswer = index === questionData.correctAnswer;
                            let buttonStyle = 'border-gray-200 bg-white hover:border-blue-300 hover:bg-gray-50';
                            if (showFeedback) {
                                if (isCorrectAnswer) buttonStyle = 'border-green-500 bg-green-50';
                                else if (isSelected && !isCorrect) buttonStyle = 'border-red-500 bg-red-50';
                                else buttonStyle = 'border-gray-200 bg-gray-50 opacity-60';
                            } else if (isSelected) {
                                buttonStyle = 'border-blue-500 bg-blue-50 shadow-md';
                            }
                            const iconContainerStyle = showFeedback && isCorrectAnswer ? 'border-green-500 bg-green-500' : showFeedback && isSelected && !isCorrect ? 'border-red-500 bg-red-500' : isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300';
                            const textStyle = showFeedback && isCorrectAnswer ? 'font-semibold text-green-700' : showFeedback && isSelected && !isCorrect ? 'font-semibold text-red-700' : isSelected ? 'font-semibold text-blue-700' : 'text-gray-700';

                            return (
                                <button key={index} onClick={() => handleAnswerSelect(index)} disabled={showFeedback} className={`w-full text-left p-5 rounded-xl border-2 transition-all duration-200 ${!showFeedback ? 'transform hover:scale-[1.02]' : ''} ${buttonStyle}`}>
                                    <div className="flex items-center gap-4">
                                        <div className={`w-8 h-8 min-w-[2rem] aspect-square rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${iconContainerStyle}`}>
                                            {showFeedback && isCorrectAnswer && <CheckCircle className="w-5 h-5 text-white" />}
                                            {showFeedback && isSelected && !isCorrect && <XCircle className="w-5 h-5 text-white" />}
                                            {!showFeedback && isSelected && <CheckCircle className="w-5 h-5 text-white" />}
                                        </div>
                                        <span className={`text-lg ${textStyle}`}>{option}</span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                    {showFeedback && (
                        <div className={`p-6 rounded-xl border-2 ${isCorrect ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
                            <div className="flex items-start gap-3 mb-3">
                                {isCorrect ? <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" /> : <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />}
                                <div className="flex-1">
                                    <h3 className={`text-xl font-bold mb-2 ${isCorrect ? 'text-green-800' : 'text-red-800'}`}>{isCorrect ? 'Correct!' : 'Incorrect'}</h3>
                                    <p className="text-gray-700 leading-relaxed">{questionData.explanation}</p>

                                    {/* Citation Display */}
                                    <CitationDisplay
                                        heading={questionData.heading}
                                        citation={getCitation(questionData.citation_id)}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="bg-white/95 backdrop-blur-sm rounded-b-3xl p-6 shadow-xl">

                    {/* Feedback Button - Full Width Above Navigation */}
                    <div className="mb-4 pb-4 border-b border-gray-200">
                        <button
                            onClick={() => setIsFeedbackModalOpen(true)}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-base bg-blue-50 text-blue-700 hover:bg-blue-100 hover:shadow-md transition-all"
                        >
                            <Flag className="w-5 h-5" />
                            Report an Issue or Provide Feedback
                        </button>
                    </div>

                    {/* Navigation Buttons */}
                    <div className="flex justify-between items-center">
                        <button onClick={onFinishSession} className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 hover:shadow-md transition-all">
                            <Home className="w-5 h-5" /> Finish Study
                        </button>
                        {!showFeedback ? (
                            <button onClick={handleSubmit} disabled={selectedAnswer === null} className={`flex items-center gap-2 px-8 py-3 rounded-xl font-semibold transition-all ${selectedAnswer === null ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:shadow-lg hover:scale-105'}`}>
                                <CheckCircle className="w-5 h-5" /> Submit Answer
                            </button>
                        ) : (
                            <button onClick={handleNext} className="flex items-center gap-2 px-8 py-3 rounded-xl font-semibold bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:shadow-lg hover:scale-105 transition-all">
                                Next Question <ChevronRight className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const LoginScreen = () => {
    const handleLogin = () => {
        const provider = new GoogleAuthProvider();
        signInWithPopup(auth, provider).catch(error => {
            console.error("Login failed:", error);
            alert("Login failed. Please try again.");
        });
    };
    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-800 to-gray-900 flex flex-col items-center justify-center text-white p-4 text-center">
            <div className="mb-8">
                {/* BRAND LOGO CONTAINER */}
                <div className="inline-block bg-white/10 backdrop-blur-sm p-6 rounded-3xl mb-6 shadow-2xl border border-white/10 transform hover:scale-105 transition-transform duration-500">
                    <LogoOption1 className="w-24 h-24 md:w-32 md:h-32" />
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Wound Ostomy Prep</h1>
                <p className="text-xl text-white/80">Your personalized study partner.</p>
            </div>
            <button onClick={handleLogin} className="flex items-center gap-4 px-8 py-4 bg-white text-gray-800 font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all">
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google logo" className="w-6 h-6" />
                Sign in with Google
            </button>
        </div>
    );
};

const MainHub = () => {
    const [currentMode, setCurrentMode] = useState('home');
    const [studyCategory, setStudyCategory] = useState(() => {
        return localStorage.getItem('studyCategory') || 'ostomy';
    });
    const [reviewParams, setReviewParams] = useState({ category: null, filter: null });
    const [singleQuestionUuid, setSingleQuestionUuid] = useState(null);
    const { user, tosAccepted, isAdmin } = useAuth();
    const handleSignOut = () => signOut(auth);

    // --- DEEP LINKING ---
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const questionUuid = params.get('question');
        if (questionUuid) {
            setSingleQuestionUuid(questionUuid);
            setCurrentMode('single_question');

            // Optional: Clean URL without refresh
            const newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
            window.history.pushState({ path: newUrl }, '', newUrl);
        }
    }, []);

    // --- INBOX POLLING ---
    const [unreadCount, setUnreadCount] = useState(0);

    const fetchUnreadCount = useCallback(async () => {
        if (!user) return;
        try {
            const token = await user.getIdToken();
            const response = await fetch(`${API_URL}/api/conversations`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                // Sum unread counts from all conversations
                const total = data.reduce((sum, conv) => sum + conv.unread_count, 0);
                setUnreadCount(total);
            }
        } catch (err) {
            console.error("Failed to fetch unread count", err);
        }
    }, [user]);

    useEffect(() => {
        if (user) {
            fetchUnreadCount();
            // Poll every 60 seconds
            const interval = setInterval(fetchUnreadCount, 60000);
            return () => clearInterval(interval);
        }
    }, [user, fetchUnreadCount]);

    // Force refresh when returning to home from inbox
    useEffect(() => {
        if (currentMode === 'home' && user) {
            fetchUnreadCount();
        }
    }, [currentMode, user, fetchUnreadCount]);

    const handleCategoryChange = (category) => {
        setStudyCategory(category);
        localStorage.setItem('studyCategory', category);
    };

    const handleReviewQuestions = (category, filter) => {
        setReviewParams({ category, filter });
        setCurrentMode('review');
    };

    if (!tosAccepted) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                <div className="text-center text-gray-700">
                    <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-slate-600" />
                    <p>Please accept the Terms of Service to continue</p>
                </div>
            </div>
        );
    }

    if (currentMode === 'settings') {
        return <SettingsPage onBack={() => setCurrentMode('home')} />;
    }

    if (currentMode === 'results') {
        return (
            <ResultsPage
                onBack={() => setCurrentMode('home')}
                onReviewQuestions={handleReviewQuestions}
            />
        );
    }

    if (currentMode === 'single_question') {
        return (
            <ReviewQuestionsPage
                onBack={() => setCurrentMode('home')}
                questionUuid={singleQuestionUuid}
            />
        );
    }

    if (currentMode === 'review') {
        return (
            <ReviewQuestionsPage
                onBack={() => setCurrentMode('results')}
                category={reviewParams.category}
                filter={reviewParams.filter}
            />
        );
    }

    if (currentMode === 'admin' && isAdmin) {
        return <AdminFeedbackPage onBack={() => setCurrentMode('home')} />;
    }

    if (currentMode === 'inbox') {
        return <UserInboxPage onBack={() => setCurrentMode('home')} />;
    }

    if (currentMode === 'home') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 relative flex flex-col md:justify-center items-center">

                {/* HEADER: Stacked on Mobile, Floating on Desktop */}
                <div className="w-full flex justify-end items-center gap-3 mb-4 md:absolute md:top-6 md:right-6 md:mb-0 md:w-auto z-20">
                    <div className="text-right">
                        <p className="font-semibold text-gray-800 text-sm md:text-base">{user?.displayName}</p>
                        <p className="text-xs text-gray-500 md:text-sm">{user?.email}</p>
                    </div>
                    <button
                        onClick={() => setCurrentMode('results')}
                        title="My Results"
                        className="p-2 md:p-3 bg-white shadow-md rounded-full text-slate-600 hover:bg-slate-50 hover:shadow-lg transition-all flex-shrink-0"
                    >
                        <BarChart3 className="w-5 h-5" />
                    </button>
                    {isAdmin && (
                        <button
                            onClick={() => setCurrentMode('admin')}
                            title="Admin Dashboard"
                            className="p-2 md:p-3 bg-yellow-500 shadow-md rounded-full text-white hover:bg-yellow-600 hover:shadow-lg transition-all flex-shrink-0 relative group"
                        >
                            <Shield className="w-5 h-5" />
                            {unreadCount > 0 && (
                                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white shadow-sm ring-2 ring-white animate-in zoom-in duration-200">
                                    {unreadCount > 9 ? '9+' : unreadCount}
                                </span>
                            )}
                            {unreadCount > 0 && (
                                <span className="absolute top-0 right-0 -mt-1 -mr-1 flex h-5 w-5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                </span>
                            )}
                        </button>
                    )}
                    {!isAdmin && (
                        <button
                            onClick={() => setCurrentMode('inbox')}
                            title="Messages"
                            className="p-2 md:p-3 bg-white shadow-md rounded-full text-slate-600 hover:bg-slate-50 hover:shadow-lg transition-all flex-shrink-0 relative group"
                        >
                            <MessageSquare className={`w-5 h-5 transition-colors ${unreadCount > 0 ? 'text-blue-600' : ''}`} />
                            {unreadCount > 0 && (
                                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white shadow-sm ring-2 ring-white animate-in zoom-in duration-200">
                                    {unreadCount > 9 ? '9+' : unreadCount}
                                </span>
                            )}
                            {unreadCount > 0 && (
                                <span className="absolute top-0 right-0 -mt-1 -mr-1 flex h-5 w-5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                </span>
                            )}
                        </button>
                    )}
                    <button
                        onClick={() => setCurrentMode('settings')}
                        title="Settings"
                        className="p-2 md:p-3 bg-white shadow-md rounded-full text-slate-600 hover:bg-slate-50 hover:shadow-lg transition-all flex-shrink-0"
                    >
                        <Settings className="w-5 h-5" />
                    </button>
                    <button
                        onClick={handleSignOut}
                        title="Sign Out"
                        className="p-2 md:p-3 bg-white shadow-md rounded-full text-slate-600 hover:bg-slate-50 hover:shadow-lg transition-all flex-shrink-0"
                    >
                        <LogOut className="w-5 h-5" />
                    </button>
                </div>

                <div className="w-full max-w-4xl">
                    {/* CENTERED BRANDING SECTION */}
                    <div className="text-center mb-8">

                        {/* NEW: Logo Display */}
                        <div className="flex justify-center mb-6">
                            <div className="bg-white p-4 rounded-2xl shadow-xl border border-gray-200">
                                <LogoOption1 className="w-16 h-16 md:w-20 md:h-20 drop-shadow-lg" />
                            </div>
                        </div>

                        <h1 className="text-3xl md:text-5xl font-bold text-gray-800 mb-6 md:mb-8 leading-tight">
                            Wound & Ostomy Prep Companion
                        </h1>

                        {/* DISCLAIMER BOX */}
                        <div className="bg-white rounded-2xl p-6 max-w-3xl mx-auto text-left border border-gray-200 shadow-lg">
                            <h3 className="text-gray-800 font-semibold text-lg mb-2 flex items-center gap-2">
                                <Sparkles className="w-5 h-5 text-amber-500" />
                                About This App
                            </h3>

                            <p className="text-gray-600 text-sm leading-relaxed mb-3">
                                This app is a supplementary resource for wound care and ostomy nurses preparing for exams. Our questions are AI-generated. While these questions and answers were vetted by a professional nurse, we cannot guarantee they are error-free. This app is not affiliated with any official certification body.
                            </p>
                            <p className="text-gray-600 text-sm leading-relaxed mb-3">
                                We encourage you to use these questions to guide your study and verify answers with official textbooks. If you spot an error, please report it using the feedback link below the answer. For all other inquiries, please email woundostomyprep@gmail.com. Thank you!
                            </p>
                        </div>
                    </div>

                    {/* STUDY CATEGORY SELECTOR */}
                    <div className="flex flex-col items-center mb-6">
                        <p className="text-gray-500 text-sm mb-2">Study Category</p>
                        <div className="bg-white shadow-md rounded-full p-1 flex border border-gray-200">
                            <button
                                onClick={() => handleCategoryChange('ostomy')}
                                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${studyCategory === 'ostomy'
                                    ? 'bg-slate-700 text-white shadow-md'
                                    : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                            >
                                Ostomy
                            </button>
                            <button
                                onClick={() => handleCategoryChange('wound_care')}
                                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${studyCategory === 'wound_care'
                                    ? 'bg-slate-700 text-white shadow-md'
                                    : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                            >
                                Wound Care
                            </button>
                        </div>
                    </div>

                    {/* ACTION BUTTONS */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 pb-8">
                        <button onClick={() => setCurrentMode('quiz')} className="group bg-white rounded-3xl p-6 md:p-8 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 text-left border border-gray-200">
                            <div className="bg-gradient-to-br from-blue-500 to-cyan-500 w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <Trophy className="w-7 h-7 md:w-8 md:h-8 text-white" />
                            </div>
                            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">Take Quiz</h2>
                            <p className="text-sm md:text-base text-gray-600 mb-6 leading-relaxed">Test your knowledge with timed multiple-choice questions.</p>
                            <div className="flex items-center text-blue-600 font-semibold group-hover:gap-3 gap-2 transition-all">Start Quiz <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></div>
                        </button>

                        <button onClick={() => setCurrentMode('study')} className="group bg-white rounded-3xl p-6 md:p-8 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 text-left border border-gray-200">
                            <div className="bg-gradient-to-br from-blue-500 to-cyan-500 w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <BookOpen className="w-7 h-7 md:w-8 md:h-8 text-white" />
                            </div>
                            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">Study Questions</h2>
                            <p className="text-sm md:text-base text-gray-600 mb-6 leading-relaxed">Learn at your own pace. Get instant feedback and explanations.</p>
                            <div className="flex items-center text-blue-600 font-semibold group-hover:gap-3 gap-2 transition-all">Start Studying <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></div>
                        </button>
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className="relative">
            <button onClick={() => setCurrentMode('home')} className="fixed top-4 left-4 md:top-6 md:left-6 z-50 flex items-center gap-2 px-4 py-2 md:px-5 md:py-3 bg-white/95 backdrop-blur-sm rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all font-semibold text-gray-700 text-sm md:text-base">
                <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" />Back to Home
            </button>
            {currentMode === 'quiz' && <QuizApp onBackToHome={() => setCurrentMode('home')} studyCategory={studyCategory} />}
            {currentMode === 'study' && <StudyQuestions onFinishSession={() => setCurrentMode('home')} studyCategory={studyCategory} />}
        </div>
    );
};


// ====================================================================
// 5. MAIN APP STRUCTURE
// ====================================================================

const App = () => {
    useEffect(() => {
        document.title = "Wound Ostomy Prep";

        // --- WAKE UP BACKEND (COLD START MITIGATION) ---
        // We fire this immediately when the app loads.
        // We use sendBeacon because it runs asynchronously without blocking
        // the page load and continues even if the user navigates away/closes tab.
        try {
            // Note: sendBeacon always sends a POST request.
            // Even if your root endpoint '/' expects GET, the 405 Method Not Allowed
            // or 404 error returned by the server is enough to wake up the container.
            const url = `${API_URL}/`;
            const data = new FormData(); // Empty data

            // Check if beacon is supported
            if (navigator.sendBeacon) {
                navigator.sendBeacon(url, data);
                console.log("Wake-up beacon sent to:", url);
            } else {
                // Fallback for older browsers
                fetch(url, { mode: 'no-cors' }).catch(() => { });
            }
        } catch (e) {
            // Ignore errors - this is just a best-effort wake-up call
            console.log("Wake-up signal failed, but app will continue.");
        }
    }, []);

    return (
        <AuthProvider>
            <CookieConsent />
            <AppContent />
        </AuthProvider>
    );
};

const AppContent = () => {
    const { user } = useAuth();
    return user ? <MainHub /> : <LoginScreen />;
}

export default App;
